import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import DynamicHeroCTA from "@/components/dashboard/DynamicHeroCTA";
import QuickStatsGrid from "@/components/dashboard/QuickStatsGrid";
import GetStartedChecklist from "@/components/dashboard/GetStartedChecklist";
import LibraryChatCard from "@/components/dashboard/LibraryChatCard";
import ActionRequiredBanner from "@/components/dashboard/ActionRequiredBanner";

const DashboardHome: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Read intent parameters from URL (set by email CTAs)
  const intentParam = searchParams.get("intent");
  const entityParam = searchParams.get("entity");
  
  const handleDismissIntent = () => {
    // Clear intent params from URL without reload
    navigate("/dashboard", { replace: true });
  };
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-quick-stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [events, challenges, invites, following] = await Promise.all([
        supabase
          .from("event_registrations")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("challenge_team_members")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("invite_logs")
          .select("id", { count: "exact", head: true })
          .eq("sent_by", user.id),
        supabase
          .from("user_follows")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
      ]);

      return {
        events: events.count ?? 0,
        challenges: challenges.count ?? 0,
        invitesSent: invites.count ?? 0,
        following: following.count ?? 0,
      };
    },
  });

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          Welcome Back
        </h1>
        <p className="text-muted-foreground mt-1 text-sm md:text-base">
          Here's what's happening with your account.
        </p>
      </div>

      {/* Action Required Banner - Shows when user lands with intent param */}
      {intentParam && (
        <ActionRequiredBanner
          intent={intentParam}
          entityId={entityParam}
          onDismiss={handleDismissIntent}
        />
      )}

      {/* Dynamic Hero CTA - Context-aware primary action */}
      <DynamicHeroCTA />

      {/* Quick Stats Grid - 4 key metrics */}
      <QuickStatsGrid 
        stats={stats ?? { events: 0, challenges: 0, invitesSent: 0, following: 0 }} 
        isLoading={isLoading} 
      />

      {/* Get Started Checklist - Persistent until complete */}
      <GetStartedChecklist />

      {/* Library Quick Access */}
      <div className="mb-6">
        <LibraryChatCard 
          title="Ask the Library"
          description="Get answers sourced from our resource library."
          className="max-w-3xl"
        />
      </div>
    </div>
  );
};

export default DashboardHome;
